import { useCallback, useEffect, useRef, useState } from 'react';
import { Modal, notification } from 'antd';
import APIClient from '../api/api';
import { InboundEmail } from '../api/models';
import EmailDetailContent from './EmailDetailContent';

const MIN_WIDTH = 480;
const MIN_HEIGHT = 400;

interface Props {
  emailId: number;
  onClose: () => void;
  onTagsChanged?: () => void;
  onPrev?: () => void;
  onNext?: () => void;
}

export default function EmailDetailModal({ emailId, onClose, onTagsChanged, onPrev, onNext }: Props) {
  const [email, setEmail] = useState<InboundEmail | null>(null);
  const [loading, setLoading] = useState(true);
  const [modalSize, setModalSize] = useState({ width: Math.round(window.innerWidth * 0.75), height: Math.round(window.innerHeight * 0.75) });
  const resizing = useRef(false);

  useEffect(() => {
    loadEmail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emailId]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') {
        e.preventDefault();
        onPrev?.();
      } else if (e.key === 'ArrowDown' || e.key === 'ArrowRight') {
        e.preventDefault();
        onNext?.();
      }
    };
    if (onPrev || onNext) {
      window.addEventListener('keydown', handler);
      return () => window.removeEventListener('keydown', handler);
    }
  }, [onPrev, onNext]);

  async function loadEmail() {
    setLoading(true);
    try {
      const resp = await APIClient.getEmail(emailId);
      setEmail(resp);
    } catch {
      notification.error({ message: 'Failed to load email.' });
    } finally {
      setLoading(false);
    }
  }

  const handleResizeStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    resizing.current = true;
    const startX = e.clientX;
    const startY = e.clientY;
    const startW = modalSize.width;
    const startH = modalSize.height;

    const onMove = (ev: MouseEvent) => {
      if (!resizing.current) return;
      const newW = Math.max(MIN_WIDTH, startW + (ev.clientX - startX) * 2);
      const newH = Math.max(MIN_HEIGHT, startH + (ev.clientY - startY));
      setModalSize({ width: newW, height: newH });
    };
    const onUp = () => {
      resizing.current = false;
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }, [modalSize]);

  const headerActions = (
    <>
      {(onPrev || onNext) && (
        <div className="flex items-center mr-1">
          <button
            type="button"
            onClick={onPrev}
            disabled={!onPrev}
            aria-label="Previous email"
            title="Previous email (↑)"
            className="w-[28px] h-[28px] rounded-l-md border border-border bg-panel flex items-center justify-center cursor-pointer text-text-3 hover:bg-bg-elevated hover:text-text disabled:opacity-30 disabled:cursor-not-allowed"
          >
            ↑
          </button>
          <button
            type="button"
            onClick={onNext}
            disabled={!onNext}
            aria-label="Next email"
            title="Next email (↓)"
            className="w-[28px] h-[28px] rounded-r-md border border-l-0 border-border bg-panel flex items-center justify-center cursor-pointer text-text-3 hover:bg-bg-elevated hover:text-text disabled:opacity-30 disabled:cursor-not-allowed"
          >
            ↓
          </button>
        </div>
      )}
      <button
        type="button"
        onClick={() => {
          const url = new URL(window.location.href);
          url.searchParams.set('email', String(emailId));
          navigator.clipboard.writeText(url.toString());
          notification.success({ message: 'Link copied.' });
        }}
        aria-label="Copy link"
        title="Copy link"
        className="w-[28px] h-[28px] rounded-md border border-border bg-panel flex items-center justify-center cursor-pointer text-text-3 hover:bg-bg-elevated hover:text-text"
      >
        ⎘
      </button>
      <button
        type="button"
        onClick={onClose}
        aria-label="Close"
        className="w-[28px] h-[28px] rounded-md border border-border bg-panel flex items-center justify-center cursor-pointer text-text-3 hover:bg-bg-elevated hover:text-text"
      >
        ✕
      </button>
    </>
  );

  return (
    <Modal
      open
      onCancel={onClose}
      footer={null}
      width={modalSize.width}
      closable={false}
      destroyOnClose
      styles={{
        content: { padding: 0, background: 'rgb(var(--color-panel) / 1)', borderRadius: 10, overflow: 'hidden', height: modalSize.height, display: 'flex', flexDirection: 'column', position: 'relative' },
        body: { padding: 0, flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' },
      }}
    >
      {loading && !email && (
        <div className="px-6 py-12 text-center text-text-3 text-[12.5px]">Loading…</div>
      )}
      {email && (
        <>
          <EmailDetailContent
            email={email}
            onTagsChanged={() => { loadEmail(); onTagsChanged?.(); }}
            headerActions={headerActions}
          />
          {/* Resize handle */}
          <div
            onMouseDown={handleResizeStart}
            className="absolute bottom-0 right-0 w-4 h-4 cursor-nwse-resize opacity-40 hover:opacity-80"
            style={{ touchAction: 'none' }}
          >
            <svg viewBox="0 0 16 16" fill="currentColor" className="text-text-3 w-full h-full">
              <path d="M14 14H12V12H14V14ZM14 10H10V12H12V14H14V10ZM14 6H6V8H8V10H10V12H12V14H14V6Z" fillRule="evenodd" />
            </svg>
          </div>
        </>
      )}
    </Modal>
  );
}
